import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SeriesHeader from '../components/brand/SeriesHeader';
import SeriesFooter from '../components/brand/SeriesFooter';
import { OverwhelmGuidedInput, GatedOverwhelmDisplay } from '../components/overwhelm-hero';
import { Button } from '../components/ui/button';
import {
  Loader2,
  Waves,
  ListOrdered,
  Compass,
  ArrowRight,
  CheckCircle,
  Sparkles,
  AlertCircle,
  Clock,
  Layers,
  Zap,
  Shield,
  Lock
} from 'lucide-react';

/**
 * HP.5: "Feeling Overwhelmed at Work?" Hero Page
 * Target: Managers/leaders in a moment of workload overload
 * Hook:   Name what you're dealing with — get a free situation map in 60 seconds
 * Maps to: U3 Situational Clarity (classifies into SITUATION_TYPES)
 * CTA:    "Describe what's overwhelming you"
 */

const OverwhelmHeroPage = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [flowState, setFlowState] = useState('hero'); // hero | input | generating | result
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Teal accent = calm / clarity (distinct from other heroes: cyan/amber/red/purple)
  const accent = '#14b8a6';
  const series = {
    name: 'Situational Clarity',
    accentColor: accent,
    slug: 'overwhelm',
    author: { name: 'Paul Hooper' }
  };

  useEffect(() => {
    try {
      const pending = sessionStorage.getItem('rv_pending_unlock');
      if (pending) {
        const data = JSON.parse(pending);
        if (data.flow_type === 'overwhelm') {
          navigate(`/overwhelm/complete?rv_id=${data.review_id}&hash=${data.input_hash}`);
        }
      }
    } catch {
      /* ignore */
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartInput = () => {
    setFlowState('input');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = () => {
    setFlowState('hero');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStartFresh = () => {
    setResult(null);
    setError(null);
    setFlowState('input');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleComplete = async (input) => {
    setFlowState('generating');
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/review/generate-overwhelm-guest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...input, flow_type: 'overwhelm' })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Classification failed');
      }
      const data = await res.json();
      setResult(data);
      setFlowState('result');
    } catch (err) {
      setError(err.message);
      setFlowState('input');
    }
  };

  const painPoints = [
    {
      icon: Waves,
      title: "You can't see the edges",
      description: "Everything feels equally urgent. You can't tell what's load-bearing and what's noise."
    },
    {
      icon: Clock,
      title: "Deadlines are colliding",
      description: "Three things are due this week. You said yes to most of them. You're not sure why anymore."
    },
    {
      icon: Layers,
      title: "You're context-switching every 5 minutes",
      description: "By 11am you've touched nine things and finished none. By 5pm you feel exhausted and behind."
    },
    {
      icon: AlertCircle,
      title: "The work that matters isn't moving",
      description: "You're busy every hour, yet the strategic work keeps slipping. It never has a deadline."
    },
    {
      icon: Compass,
      title: "Priorities keep changing on you",
      description: "Your manager, stakeholders and team each pull in a different direction. You're the buffer."
    },
    {
      icon: Sparkles,
      title: "You know you need a ritual — you don't have one",
      description: "Friday prioritisation would help. Morning review would help. Neither has survived the chaos yet."
    }
  ];

  const howItWorks = [
    {
      step: 1,
      title: 'Describe what you\'re dealing with',
      description: 'Free-form, in your own words. No polish needed. The messier the better.',
      icon: Waves
    },
    {
      step: 2,
      title: 'We map it to a known pattern',
      description: 'We classify your situation against 10 common leadership overwhelm patterns — time pressure, execution gap, expectation clarity, and more.',
      icon: Compass
    },
    {
      step: 3,
      title: 'Get reframes + a triage plan',
      description: 'Two reframes free; the full 3-step triage plan and a companion chat session unlock with a free account.',
      icon: ListOrdered
    }
  ];

  // ═══ GENERATING STATE ═══
  if (flowState === 'generating') {
    return (
      <div className="min-h-screen bg-gray-950">
        <SeriesHeader series={series} />
        <main className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center" data-testid="overwhelm-generating">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: `${accent}20` }}>
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: accent }} />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Mapping your situation...</h2>
            <p className="text-gray-400 max-w-md mx-auto">Finding the pattern underneath the noise. This takes a few seconds.</p>
          </div>
        </main>
        <SeriesFooter series={series} />
      </div>
    );
  }

  // ═══ INPUT STATE ═══
  if (flowState === 'input') {
    return (
      <div className="min-h-screen bg-gray-950">
        <SeriesHeader series={series} />
        <main className="max-w-4xl mx-auto px-4 py-12">
          {error ? (
            <div data-testid="overwhelm-error" className="mb-6 p-4 bg-red-900/20 border border-red-800/30 rounded-lg">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          ) : null}
          <OverwhelmGuidedInput onComplete={handleComplete} onCancel={handleCancel} accentColor={accent} />
        </main>
        <SeriesFooter series={series} />
      </div>
    );
  }

  // ═══ RESULT STATE ═══
  if (flowState === 'result') {
    return (
      <div className="min-h-screen bg-gray-950">
        <SeriesHeader series={series} />
        <main className="max-w-4xl mx-auto px-4 py-12">
          <GatedOverwhelmDisplay data={result} onStartFresh={handleStartFresh} accentColor={accent} />
        </main>
        <SeriesFooter series={series} />
      </div>
    );
  }

  // ═══ HERO (DEFAULT) ═══
  return (
    <div data-testid="overwhelm-hero-page" className="min-h-screen bg-gray-950">
      <SeriesHeader series={series} />

      <main>
        {/* HERO */}
        <section data-testid="overwhelm-hero-section" className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-teal-950/20 via-gray-950 to-gray-950" />
          <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-28">
            <div className="max-w-3xl">
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-6 border"
                style={{ borderColor: `${accent}40`, color: accent, backgroundColor: `${accent}10` }}
              >
                <Waves className="w-3.5 h-3.5" /> Situational Clarity — Free Map
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
                Feeling overwhelmed<br />
                <span style={{ color: accent }}>at work?</span>
              </h1>

              <p className="text-lg md:text-xl text-gray-300 mb-4 max-w-2xl leading-relaxed">
                Overwhelm is almost never about time. It's about an un-mapped situation.
              </p>
              <p className="text-base text-gray-400 mb-10 max-w-2xl">
                Describe what you're dealing with — in 60 seconds we'll name the pattern underneath and give you two reframes. The full triage plan is free with signup.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  data-testid="overwhelm-start-btn"
                  onClick={handleStartInput}
                  className="text-white font-semibold px-8 py-6 text-base rounded-xl shadow-lg"
                  style={{ backgroundColor: accent }}
                >
                  Describe what's overwhelming you
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => document.getElementById('overwhelm-how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                  className="border-gray-700 text-gray-300 hover:text-white px-6 py-6 text-base rounded-xl"
                >
                  How does it work?
                </Button>
              </div>

              <p className="text-xs text-gray-500 mt-4">
                No signup required for the free map. Takes about 60 seconds.
              </p>
            </div>
          </div>
        </section>

        {/* PAIN POINTS */}
        <section data-testid="overwhelm-pain-points" className="py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Sound familiar?</h2>
              <p className="text-gray-400 max-w-lg mx-auto">
                Overwhelm feels personal — but the underlying patterns are common and nameable.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {painPoints.map((point, i) => {
                const Icon = point.icon;
                return (
                  <div key={i} className="p-5 bg-gray-900/50 border border-gray-800/50 rounded-xl hover:border-gray-700 transition-colors">
                    <Icon className="w-5 h-5 mb-3" style={{ color: accent }} />
                    <h3 className="text-white font-semibold text-sm mb-1.5">{point.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{point.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section id="overwhelm-how-it-works" data-testid="overwhelm-how-it-works" className="py-20 px-4 bg-gray-900/30">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">How it works</h2>
              <p className="text-gray-400">Three steps. Sixty seconds. Actionable clarity.</p>
            </div>
            <div className="space-y-6">
              {howItWorks.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.step} className="flex items-start gap-5 p-6 bg-gray-950/50 border border-gray-800/50 rounded-xl">
                    <div
                      className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm"
                      style={{ backgroundColor: `${accent}20`, color: accent }}
                    >
                      {item.step}
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1 flex items-center gap-2">
                        <Icon className="w-4 h-4" style={{ color: accent }} />
                        {item.title}
                      </h3>
                      <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* SAMPLE OUTPUT */}
        <section data-testid="overwhelm-demo-output" className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">What you'll see</h2>
              <p className="text-gray-400">A pattern-named situation map and two reframes — before anyone asks for your email.</p>
            </div>
            <div className="p-6 bg-gray-900/60 border border-gray-800 rounded-xl">
              <div className="flex items-center gap-2 mb-4">
                <Compass className="w-4 h-4" style={{ color: accent }} />
                <span className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Sample Situation Map</span>
              </div>

              <div
                className="p-4 rounded-lg mb-4"
                style={{ backgroundColor: `${accent}08`, borderColor: `${accent}20`, borderWidth: 1, borderStyle: 'solid' }}
              >
                <p className="text-white font-semibold mb-1">Time Pressure</p>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Overload, competing demands, prioritization challenges. ✓ Good match — signals: "deadline", "urgent"
                </p>
              </div>

              <div className="space-y-2 mb-4">
                <div className="p-3 bg-gray-800/40 rounded-lg border border-gray-700/30 text-sm text-gray-200">
                  Overwhelm is rarely about time — it's usually about competing priorities with unclear owners.
                </div>
                <div className="p-3 bg-gray-800/40 rounded-lg border border-gray-700/30 text-sm text-gray-200">
                  The items that feel 'equally important' almost never are. One is load-bearing; the rest are noise.
                </div>
              </div>

              <div className="relative p-4 rounded-lg border border-dashed border-gray-700 overflow-hidden">
                <div className="absolute inset-0 backdrop-blur-sm bg-gray-900/70 z-10 flex items-center justify-center">
                  <div className="text-center">
                    <Lock className="w-5 h-5 mx-auto mb-2" style={{ color: accent }} />
                    <p className="text-gray-300 text-xs font-medium">3-step triage plan + companion session</p>
                  </div>
                </div>
                <div className="opacity-20 select-none space-y-2">
                  <div className="h-4 bg-gray-700/50 rounded w-3/4" />
                  <div className="h-3 bg-gray-700/30 rounded w-full" />
                  <div className="h-4 bg-gray-700/50 rounded w-1/2" />
                  <div className="h-3 bg-gray-700/30 rounded w-5/6" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section data-testid="overwhelm-final-cta" className="py-20 px-4 bg-gray-900/30">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              You're not behind. You're in an un-mapped situation.
            </h2>
            <p className="text-gray-400 mb-8 max-w-lg mx-auto">
              Sixty seconds of input. A named pattern. Two reframes. Then — if you want — a full triage plan and a companion ready to work through it with you.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10 max-w-2xl mx-auto">
              {[
                { icon: CheckCircle, text: 'Free — no card required' },
                { icon: Zap, text: 'Instant classification' },
                { icon: Shield, text: 'Nothing stored without signup' }
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex items-center justify-center gap-2 text-sm text-gray-300">
                    <Icon className="w-4 h-4" style={{ color: accent }} />
                    {item.text}
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                data-testid="overwhelm-final-cta-btn"
                onClick={handleStartInput}
                className="text-white font-semibold px-8 py-6 text-base rounded-xl shadow-lg"
                style={{ backgroundColor: accent }}
              >
                Describe what's overwhelming you
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/insight')}
                className="border-gray-700 text-gray-300 hover:text-white px-6 py-6 text-base rounded-xl"
              >
                Try Capability Insight instead
              </Button>
            </div>
          </div>
        </section>
      </main>

      <SeriesFooter series={series} />
    </div>
  );
};

export default OverwhelmHeroPage;
